import { memo } from 'react'

import { IPartner } from 'clickNgo/entities/partner/models'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { CopiedText } from 'shared/components/copiedText'

import { partnerGeneralInfoPanelTranslation } from './translation'

import styles from './PartnerGeneralInfoPanel.module.scss'

type TProps = Partial<
  Pick<
    IPartner,
    'partnerId' | 'locationId' | 'communicationName' | 'onboardingRequestId' | 'hierarchyType'
  >
>

export const PartnerGeneralInfoPanel = memo<TProps>(
  ({ communicationName, partnerId, locationId, onboardingRequestId, hierarchyType }) => {
    const hasPartnerId = Boolean(partnerId)

    const isDummyPartner =
      hasPartnerId && (partnerId!.startsWith('new-') || partnerId!.startsWith('portal-'))

    return (
      <div className={styles.container}>
        <RenderWhen condition={hasPartnerId}>
          <div className={styles.row}>
            <div className={styles.label}>
              <Translation text={partnerGeneralInfoPanelTranslation.labels.partnerId} span />
            </div>
            <strong className={styles.value}>
              <RenderWhen condition={isDummyPartner}>
                <Translation text={partnerGeneralInfoPanelTranslation.default.partnerId} span />
              </RenderWhen>
              <RenderWhen condition={!isDummyPartner}>
                <CopiedText>{partnerId!}</CopiedText>
              </RenderWhen>
            </strong>
          </div>
        </RenderWhen>
        <div className={styles.row}>
          <div className={styles.label}>
            <Translation text={partnerGeneralInfoPanelTranslation.labels.locationId} span />
          </div>
          <strong className={styles.value}>
            <RenderWhen condition={Boolean(locationId)}>
              <CopiedText>{locationId!}</CopiedText>
            </RenderWhen>
            <RenderWhen condition={!locationId}>-</RenderWhen>
          </strong>
        </div>
        <RenderWhen condition={Boolean(onboardingRequestId)}>
          <div className={styles.row}>
            <div className={styles.label}>
              <Translation
                text={partnerGeneralInfoPanelTranslation.labels.onboardingRequestId}
                span
              />
            </div>
            <strong className={styles.value}>
              <RenderWhen condition={Boolean(onboardingRequestId)}>
                <CopiedText>{String(onboardingRequestId)}</CopiedText>
              </RenderWhen>
              <RenderWhen condition={!onboardingRequestId}>-</RenderWhen>
            </strong>
          </div>
        </RenderWhen>
        <div className={styles.row}>
          <div className={styles.label}>
            <Translation text={partnerGeneralInfoPanelTranslation.labels.communicationName} span />
          </div>
          <strong className={styles.value}>
            <RenderWhen condition={Boolean(communicationName)}>
              <CopiedText>{communicationName!}</CopiedText>
            </RenderWhen>
            <RenderWhen condition={!communicationName}>-</RenderWhen>
          </strong>
        </div>
        <RenderWhen condition={Boolean(hierarchyType)}>
          <div className={styles.row}>
            <div className={styles.label}>
              <Translation text={partnerGeneralInfoPanelTranslation.labels.hierarchyType} span />
            </div>
            <strong className={styles.value}>
              <RenderWhen condition={Boolean(communicationName)}>
                <CopiedText>{hierarchyType!}</CopiedText>
              </RenderWhen>
              <RenderWhen condition={!communicationName || !hierarchyType}>-</RenderWhen>
            </strong>
          </div>
        </RenderWhen>
      </div>
    )
  }
)

PartnerGeneralInfoPanel.displayName = 'PartnerGeneralInfoPanel'
